import api from './api';

export interface Meeting {
  id: number;
  title: string;
  scheduled_at: string;
  duration_minutes: number;
  notes: string | null;
  fee: number | null;
  status: 'active' | 'noshow' | 'cancelled' | 'done';
  yoman_id: string | null;
  yoman_url: string | null;
  team_url: string | null;
  patient_id: number | null;
  therapist_id: number;
  tenant_id: number;
  meeting_type_id: number | null;
  /** Absent for a meeting the Israeli system owns: its patient lives in Xano,
   *  not here, and only external_patient_ref identifies them. Use
   *  patientLabel(meeting) rather than reading full_name directly. */
  patient: {
    id: number;
    /** Their scheduling page is keyed by this. */
    uuid?: string | null;
    full_name: string;
    email: string | null;
    phone?: string | null;
    hm_id: string;
    id_number?: string | null;
    gp_email?: string | null;
  } | null;
  /** The Israeli patient id, when the patient is not in this database. */
  external_patient_ref?: string | null;
  therapist: {
    id: number;
    full_name: string;
    email: string;
    license_number?: string | null;
  };
  meeting_type: {
    id: number;
    name: string;
    user_type: string;
    type: string | null;
    summary_type_id?: number | null;
    summary_type?: {
      id: number;
      name: string;
      summary_sections?: any[] | null;
      pdf_layout?: any[] | null;
    } | null;
    questionnaire_type_ids?: number[] | null;
  } | null;
  created_at: string;
  updated_at: string;
  cm_comments: string | null;
  psy_recommendations: string | null;
  psy_summary_and_discussion: string | null;
  primary_diagnostic: string | null;
  secondary_diagnostic: string | null;
  diagnostic_notes: string | null;
  adhd_medication_decision: string | null;
  adhd_not_diagnosed_reason: string | null;
  no_adhd_medication_reason_and_notes: string | null;
  adhd_medication_deferred_reason_and_notes: string | null;
  adhd_medication_deferred_reasons: string[] | null;
  medication_id: number | null;
  medication: { id: number; name: string } | null;
  titration_plan: string | null;
  initial_plan_notes: string | null;
  monitoring_plan: string | null;
  additional_monitoring_notes: string | null;
  need_follow_up: boolean | null;
  follow_up_weeks: number | null;
  titration_schedule: TitrationSchedule | null;
  titration_comments: string | null;
  treatment_decision: string | null;
  treatment_decision_reasons: string[] | null;
  treatment_decision_notes: string | null;
  bot_ids: string[] | null;
  source: string | null;
  reason_of_cancelation: string | null;
  cancellation_note: string | null;
  cancelation_time: string | null;
  summary_prompt_id: number | null;
  summary_saved: boolean;
  summary_ready: boolean;
  summary_approved: boolean;
  summary_sent: boolean;
  has_transcript: boolean;
  has_rt_transcript: boolean;
}

export interface MeetingCreate {
  title: string;
  scheduled_at: string;
  duration_minutes?: number;
  notes?: string;
  fee?: number;
  yoman_id?: string;
  patient_id: number;
  therapist_id: number;
  tenant_id: number;
  meeting_type_id?: number;
}

export interface MeetingUpdate {
  title?: string;
  scheduled_at?: string;
  duration_minutes?: number;
  notes?: string;
  fee?: number;
  status?: 'active' | 'noshow' | 'cancelled' | 'done';
  yoman_id?: string;
  meeting_type_id?: number;
  reason_of_cancelation?: string;
  cancellation_note?: string;
  cm_comments?: string;
  psy_recommendations?: string;
  psy_summary_and_discussion?: string;
  summary_approved?: boolean;
  summary_sent?: boolean;
  has_transcript?: boolean;
  primary_diagnostic?: string | null;
  secondary_diagnostic?: string | null;
  diagnostic_notes?: string | null;
  adhd_medication_decision?: string | null;
  adhd_not_diagnosed_reason?: string | null;
  no_adhd_medication_reason_and_notes?: string | null;
  adhd_medication_deferred_reason_and_notes?: string | null;
  adhd_medication_deferred_reasons?: string[] | null;
  medication_id?: number | null;
  titration_plan?: string | null;
  initial_plan_notes?: string | null;
  monitoring_plan?: string | null;
  additional_monitoring_notes?: string | null;
  need_follow_up?: boolean | null;
  follow_up_weeks?: number | null;
  titration_schedule?: TitrationSchedule | null;
  titration_comments?: string | null;
  treatment_decision?: string | null;
  treatment_decision_reasons?: string[] | null;
  treatment_decision_notes?: string | null;
}

export interface TitrationWeek {
  week: number;
  dose: string;
  instructions: string;
}

export interface TitrationSchedule {
  medication: {
    medication_id: number | null;
    dosageInstruction: string;
    quantity: number | null;
  };
  durationWeeks: number;
  sameDoseAllWeeks: boolean;
  weeks: TitrationWeek[];
  howToTake: string;
  sideEffects: string[];
  sideEffectsOther: string;
}

export const meetingService = {
  async getMeetings(
    tenantId: number,
    patientId?: number,
    therapistId?: number,
    status?: 'active' | 'noshow' | 'cancelled' | 'done' | 'all',
    dateRange?: string,
    startDate?: string,  // ISO format: YYYY-MM-DD
    endDate?: string,    // ISO format: YYYY-MM-DD
    excludeCancelled?: boolean
  ): Promise<Meeting[]> {
    const params = new URLSearchParams({ tenant_id: tenantId.toString() });
    if (patientId) params.append('patient_id', patientId.toString());
    if (therapistId) params.append('therapist_id', therapistId.toString());
    if (status && status !== 'all') params.append('status', status);
    if (excludeCancelled) params.append('exclude_cancelled', 'true');

    // Custom date range takes precedence over predefined ranges
    if (startDate) params.append('start_date', startDate);
    if (endDate) params.append('end_date', endDate);

    // Only add date_range if no custom dates provided
    if (!startDate && !endDate && dateRange) {
      params.append('date_range', dateRange);
    }

    const response = await api.get(`/meetings/?${params}`);
    return response.data;
  },

  /** The patient's own scheduling page, as a link this clinician may send.
   *
   *  Minted by the server, not assembled here: it carries a signed marker
   *  naming the signed-in user, so a booking made through it is recorded as
   *  arranged by them rather than as the patient's own. A link the browser
   *  built could name anyone.
   */
  async getScheduleLink(patientId: number): Promise<{ link: string }> {
    const response = await api.get(`/meetings/schedule-link/${patientId}`);
    return response.data;
  },

  async getMeeting(meetingId: number): Promise<Meeting> {
    const response = await api.get(`/meetings/${meetingId}`);
    return response.data;
  },

  async createMeeting(meetingData: MeetingCreate): Promise<Meeting> {
    const response = await api.post('/meetings/', meetingData);
    return response.data;
  },

  async updateMeeting(meetingId: number, meetingData: MeetingUpdate): Promise<Meeting> {
    const response = await api.put(`/meetings/${meetingId}`, meetingData);
    return response.data;
  },

  async updateMeetingStatus(
    meetingId: number,
    status: 'active' | 'noshow' | 'cancelled' | 'done'
  ): Promise<void> {
    await api.post(`/meetings/${meetingId}/status/${status}`);
  },

  async summarizeMeeting(meetingId: number): Promise<{
    message: string;
    job_id: number;
    status: string;
  }> {
    const response = await api.post(`/meetings/${meetingId}/summarize`);
    return response.data;
  },

  async getCMSummary(meetingId: number): Promise<{ content: string; is_ai_generated: boolean; summary_approved: boolean }> {
    const response = await api.get(`/meetings/${meetingId}/cm-summary`);
    return response.data;
  },

  async updateCMSummary(meetingId: number, content: string, approve = false): Promise<{ content: string; is_ai_generated: boolean; summary_approved: boolean }> {
    const response = await api.put(`/meetings/${meetingId}/cm-summary`, { content, approve });
    return response.data;
  },

  async sendEmail(meetingId: number, templateId: number): Promise<void> {
    await api.post(`/meetings/${meetingId}/send-email`, { template_id: templateId });
  },

  async sendSms(meetingId: number, templateId: number): Promise<void> {
    await api.post(`/meetings/${meetingId}/send-sms`, { template_id: templateId });
  },

  async startMeeting(meetingId: number): Promise<{ meeting_id: number; bot_ids: string[] }> {
    const response = await api.post(`/meetings/${meetingId}/start`);
    return response.data;
  },

  async sendSummary(meetingId: number, recipients?: { patientEmail?: string | null; gpEmail?: string | null }, adhdIntake?: boolean): Promise<{
    summary_sent: boolean;
    pdf_saved: boolean;
    summary_pdf_s3_key: string | null;
    email_sent: boolean;
    gp_email_sent: boolean;
    sms_sent: boolean;
  }> {
    const body: Record<string, any> = {};
    if (recipients?.patientEmail != null) body.patient_email = recipients.patientEmail;
    if (recipients?.gpEmail != null) body.gp_email = recipients.gpEmail;
    // An omitted address does NOT mean "skip the patient": the backend falls
    // back to patient.email, so unticking them used to mail them anyway. Only
    // an explicit false suppresses it.
    if (recipients && recipients.patientEmail == null) body.send_to_patient = false;
    if (adhdIntake) body.adhd_intake = true;
    const response = await api.post(`/meetings/${meetingId}/send-summary`, body);
    return response.data;
  },

  async generateSummaryPdf(meetingId: number): Promise<Blob> {
    const response = await api.get(`/meetings/${meetingId}/summary-pdf`, {
      responseType: 'blob',
    });
    return response.data;
  },
};